
import DatePicker from 'vue2-datepicker3';
import 'vue2-datepicker3/index.css';
import Vue from 'vue';
import Moment from 'moment';

// Local variables
let shortcutUsed = false;
let shortcutText = '';
const DAY = 3600 * 1000 * 24;
const DATE_FORMAT = 'MMMM, DD, YYYY';
const CAMPAIGN_TEXT = 'Campaign period';

interface IShortcut {
  text: string;
  onClick: Function;
}

interface IData {
  inputValue: string;
  value: Date[];
  settings: {
    formatLocale: {
      firstDayOfWeek: number;
      firstWeekContainsDate: number;
    };
  };
  shortcuts: IShortcut[];
  open: boolean;
}

export default Vue.extend({
  components: {
    DatePicker,
  },
  props: {
    range: {
      type: Array,
      default() {
        return [];
      },
    },
    handleCampaignRange: {
      type: Boolean,
      default: false,
    },
  },
  data(): IData {
    return {
      inputValue: '',
      value: [],
      open: false,
      settings: {
        formatLocale: {
          firstDayOfWeek: 1,
          firstWeekContainsDate: 1
        },
      },
      shortcuts: [
        {
          text: 'Today',
          onClick() {
            const date = new Date();
            shortcutUsed = true;
            shortcutText = 'Today';
            return [date, date];
          },
        },
        {
          text: 'Yesterday',
          onClick() {
            const date = new Date();
            date.setTime(date.getTime() - DAY);
            shortcutUsed = true;
            shortcutText = 'Yesterday';
            return [date, date];
          },
        },
        {
          text: 'Last 7 days',
          onClick() {
            const date1 = new Date();
            const date2 = new Date();
            date1.setTime(date1.getTime() - DAY * 7);
            shortcutUsed = true;
            shortcutText = 'Last 7 days';
            return [date1, date2];
          },
        },
        {
          text: 'Last 30 days',
          onClick() {
            const date1 = new Date();
            const date2 = new Date();
            date1.setTime(date1.getTime() - DAY * 30);
            shortcutUsed = true;
            shortcutText = 'Last 30 days';
            return [date1, date2];
          },
        },
        {
          text: 'This month',
          onClick() {
            const date1 = new Date();
            const date2 = new Date();
            date1.setDate(1);
            shortcutUsed = true;
            shortcutText = 'This month';
            return [date1, date2];
          },
        },
        {
          text: 'Previous month',
          onClick() {
            const date1 = new Date();
            const date2 = new Date();
            date1.setMonth(date1.getMonth() - 1);
            date1.setDate(1);
            // This jumps back to last day of the previous month
            date2.setDate(0);
            shortcutUsed = true;
            shortcutText = 'Previous month';
            return [date1, date2];
          },
        },
      ],
    };
  },
  computed: {
    selectedCampaign(): any {
      if (
        !this.$store.state['advertiser-insights'] ||
        !this.handleCampaignRange
      ) {
        return null;
      }
      return this.$store.state['advertiser-insights'].filters.selectedCampaign;
    },
  },
  watch: {
    selectedCampaign: {
      handler(val) {
        if (!val) {
          return;
        }
        this.setCampaignRange();
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    // Add the Campaign period option to the date range shortcuts
    if (this.handleCampaignRange) {
      this.shortcuts.push({
        text: CAMPAIGN_TEXT,
        onClick() {
          // Function will be replaced later on with correct dates
        },
      });
    }

    // Check if dates already exists in query params, and go with that
    if (this.$route.query.date) {
      if (
        this.$route.query.date.includes('~') &&
        typeof this.$route.query.date === 'string'
      ) {
        // Dates in the query param
        const split = this.$route.query.date.split('~');
        // Fix timezone mess
        const date1 = Moment(split[0]);
        const date2 = Moment(split[1]);
        if (date1.utcOffset() > 0) {
          date1.add(date1.utcOffset(), 'minutes');
          date2.add(date2.utcOffset(), 'minutes');
        } else {
          date1.subtract(date1.utcOffset(), 'minutes');
          date2.subtract(date2.utcOffset(), 'minutes');
        }
        this.setRangeDate([date1.toDate(), date2.toDate()]);
        // Set datepicker values
        this.value = [date1.toDate(), date2.toDate()];
        return;
      } else if (
        this.$route.query.date === CAMPAIGN_TEXT &&
        this.handleCampaignRange
      ) {
        // Just return here, the dates will be set automaticaly when the correct campaign is selected.
        return;
      } else {
        // Probably dynamic shortcut in query param
        const date = this.$route.query.date;
        const found = this.shortcuts.find(s => s.text === date);
        if (found) {
          const dates = found.onClick();
          this.setRangeDate(dates);
          this.value = dates;
          return;
        }
      }
    }
    // If no query param is present and campaigns are handled, return.
    // The query param and dates will be set later on.
    if (this.handleCampaignRange) {
      return;
    }
    // Activating initial shortcut for the datepicker
    this.setDefaultRange();
  },
  methods: {
    setDefaultRange() {
      // Default at the moment is "This month"
      const dates = this.shortcuts[4].onClick();
      this.setRangeDate(dates);
      this.value = dates;
    },
    setCampaignRange() {
      // See if the campaign period option is in the list
      const find = this.shortcuts.find(s => s.text === CAMPAIGN_TEXT);
      // All campaigns has been selected
      if (!this.selectedCampaign.id) {
        if (find) {
          this.shortcuts.pop();
        }
        if (
          this.$route.query.date === CAMPAIGN_TEXT ||
          !this.$route.query.date
        ) {
          this.setDefaultRange();
        }
        return;
      }

      if (find) {
        // We need to remove the old Campaign period option, so pop and forget it.
        this.shortcuts.pop();
      }

      // Now, create the new Campaign period option with correct dates and all,
      // and push it to shortcuts.
      const that = this;
      const newShortcut = {
        text: CAMPAIGN_TEXT,
        onClick() {
          const dates = [
            new Date(that.selectedCampaign.createdAt),
            new Date(that.selectedCampaign.endDate),
          ];
          shortcutUsed = true;
          shortcutText = CAMPAIGN_TEXT;
          that.setRangeDate(dates);
          that.value = dates;
        },
      };
      this.shortcuts.push(newShortcut);

      // If the query param already is Campaign period or dont exists, activate the new settings
      if (this.$route.query.date === CAMPAIGN_TEXT || !this.$route.query.date) {
        newShortcut.onClick();
      }
    },
    async setRangeDate(val: Date[]) {
      if (shortcutUsed) {
        shortcutUsed = false;
        this.setInputDate(shortcutText);
        if (this.$route.query.date !== shortcutText) {
          await this.$router.push({
            query: Object.assign({}, this.$route.query, { date: shortcutText }),
          });
        }
        shortcutText = '';
      } else {
        const day1 = Moment(val[0]);
        const day2 = Moment(val[1]);
        this.setInputDate(
          `${day1.format(DATE_FORMAT)} - ${day2.format(DATE_FORMAT)}`
        );
        // Set query parameters
        // Skip if initial is false, OR when datepicker is calling this function
        // initial will be a string in that case
        const date = `${day1.format('YYYY-MM-DD')}~${day2.format(
          'YYYY-MM-DD'
        )}`;
        if (this.$route.query.date !== date) {
          await this.$router.push({
            query: Object.assign({}, this.$route.query, {
              date,
            }),
          });
        }
      }
      this.$emit('input', [val[0], val[1]], this.inputValue);
    },
    setInputDate(val) {
      this.inputValue = val;
    },
    toogleDatePicker() {
      this.open = !this.open;
    },
    closeDatePicker() {
      this.open = false;
    },
  },
});
